/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Component, OnDestroy, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	UntypedFormControl,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { NumberUtils } from 'src/app/utils/number-utils';
import * as strings from '@touchplan/strings';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { DefaultLoggerService } from '@touchplan/logging';

@Component({
	selector: 'mat-number-spinner',
	templateUrl: './mat-number-spinner.component.html',
	styleUrls: ['./mat-number-spinner.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: MatNumberSpinnerComponent,
		},
		{
			provide: NG_VALIDATORS,
			useExisting: MatNumberSpinnerComponent,
			multi: true,
		},
		LoggerServiceFactory.provide('MatNumberSpinnerComponent'),
	],
})
export class MatNumberSpinnerComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
	private readonly defaultMin = 0;
	private readonly defaultMax = 9999;
	private readonly defaultStep = 1;

	@Input() public label = '';
	@Input() public errorText = '';
	@Input() public min = this.defaultMin;
	@Input() public max = this.defaultMax;
	@Input() public step = this.defaultStep;
	@Input() public widthOfTextInChars = 3;
	@Input() public fontSize = '20px';
	@Input() public floatingPoint = false;
	@Input() public roundAfterPlusOrMinus = false;

	public value: number;
	public textFormControl: UntypedFormControl;
	public componentIsDisabled = false;

	private valueChangeSub: Subscription;
	//private formatError: ValidationErrors;
	private onTouched: () => void;
	private onValueChange = (thing: any) => {};

	constructor(private logger: DefaultLoggerService) {
		this.textFormControl = new UntypedFormControl('', [
			this.validateNumber,
			Validators.min(this.defaultMin),
			Validators.max(this.defaultMax),
		]);
	}

	public ngOnInit(): void {
		this.processConfigParams();
		this.startInternalSubscription();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		this.processConfigParams();
	}

	// runs the internal valuechanges subscription
	private startInternalSubscription() {
		this.valueChangeSub = this.textFormControl.valueChanges.subscribe((newText) => {
			// Clean-up leading and/or trailing spaces since we have limited number real-estate:
			const trim = newText.trim();
			if (trim.length !== newText.length) {
				this.textFormControl.setValue(trim);
			} else {
				// Check validity:
				const newValue = this.stringToNumber(newText);
				if (this.textFormControl.valid && typeof newValue === 'number' && newValue !== this.value) {
					this.value = newValue;
					this.onValueChange(newValue); // the new text is valid
				} else {
					// Invalid
					// TP-3340:
					// DON'T report this as a value change;
					// only valid values should be reported.
				}
			}
			this.updateDisplayedValidityStatus();
		});
	}

	private processConfigParams(): void {
		let initialValue = this.accommodateStringsForNumberInputValues(this.value, 'value');
		let inputMin = this.accommodateStringsForNumberInputValues(this.min, 'min');
		if (inputMin === null) {
			inputMin = this.defaultMin;
		}
		let inputMax = this.accommodateStringsForNumberInputValues(this.max, 'max');
		if (inputMax === null) {
			inputMax = this.defaultMax;
		}

		let inputStep = this.accommodateStringsForNumberInputValues(this.step, 'step');
		if (inputStep === null) {
			inputStep = this.defaultStep;
		} else if (inputStep !== Math.floor(inputStep)) {
			this.logger.error("@Input() parameter, [step], can't be a floating point value");
			inputStep = this.defaultStep;
		}
		this.step = inputStep;

		// If value, min or max were given as floating point, but [floatingPoint] is false,
		// then adjust value, min and/or max accordingly:
		if (!this.floatingPoint) {
			const minCeil = Math.ceil(inputMin);
			if (minCeil !== inputMin) {
				this.logger.error('@Input() parameter, [min], is not an integer but [floatingPoint] is false');
				inputMin = minCeil;
			}
			const maxFloor = Math.floor(inputMax);
			if (maxFloor !== inputMax) {
				this.logger.error('@Input() parameter, [max], is not an integer but [floatingPoint] is false');
				inputMax = maxFloor;
			}
			if (initialValue !== null && initialValue !== undefined) {
				const valueCeil = Math.ceil(initialValue);
				if (valueCeil !== initialValue) {
					this.logger.error('The initial value is not an integer but [floatingPoint] is false');
					initialValue = inputMin;
				}
			}
		}

		// Sanity checks on those inputs:
		if (inputMin > inputMax) {
			this.logger.error('@Input() parameter, [min], is greater than @Input parameter, [max]');
		} else {
			this.min = inputMin;
			this.max = inputMax;
			this.textFormControl.setValidators([
				this.validateNumber,
				Validators.min(this.min),
				Validators.max(this.max),
			]);
			this.textFormControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		}

		if (initialValue < this.min) {
			this.logger.error('@Input() parameter, [value], is less than @Input parameter, [min]');
			initialValue = this.min;
		} else if (initialValue > this.max) {
			this.logger.error('@Input() parameter, [value], is greater than @Input parameter, [max]');
			initialValue = this.max;
		}
		this.value = initialValue;

		// Deal with initializing value, including proper FormControl stuff:
		this.writeValueInternal(initialValue);
	}

	// Sometimes people mistakenly put quotes around number @Input values.
	// This helps to accommodate that sort of fairly benign thing...
	private accommodateStringsForNumberInputValues(value: any, inputName: string): number {
		if (value === null || value === undefined) {
			return value;
		}
		if (this.isString(value)) {
			const n = this.stringToNumber(value);
			if (typeof n === 'number') {
				return n as number;
			}
			this.logger.error("@Input() parameter, '" + inputName + "', has an invalid value", value);
			return null;
		}
		return value as number;
	}

	private stringToNumber(s: string): ValidationErrors | number {
		return NumberUtils.stringToNumberWithValidation(s, this.floatingPoint, false, this.label);
	}

	private validateNumber = (control: AbstractControl): ValidationErrors | null => {
		const n = this.stringToNumber(control.value);
		// Really should be the following:
		// return typeof n !== 'number' ? { invalidNumber: n } : null;
		return typeof n !== 'number' ? n : null;
	};

	public handleMinusClick() {
		let newValue;
		// If there's currently a validation error, on a click, restore the last valid value (or "min", if nothing was valid):
		if (!this.textFormControl.valid) {
			newValue = this.value === null || this.value === undefined ? this.min : this.value;
		} else {
			newValue = NumberUtils.decrement(this.value, this.min, this.step);
		}
		if (newValue < this.min) {
			newValue = this.min;
		}
		if (this.roundAfterPlusOrMinus) {
			newValue = Math.max(Math.round(newValue), this.min);
		}
		this.writeValueInternal(newValue);
		this.onNumberSpinnerTouched();
	}

	public handlePlusClick() {
		let newValue;
		// If there's currently a validation error, on a click, restore the last valid value (or "max", if nothing was valid):
		if (!this.textFormControl.valid) {
			newValue = this.value === null || this.value === undefined ? this.max : this.value;
		} else {
			newValue = NumberUtils.increment(this.value, this.max, this.step);
		}
		if (newValue > this.max) {
			newValue = this.max;
		}
		if (this.roundAfterPlusOrMinus) {
			newValue = Math.min(Math.round(newValue), this.max);
		}
		this.writeValueInternal(newValue);
		this.onNumberSpinnerTouched();
	}

	public onTextFocus(): void {
		// While the user is in the text box, clear any error on the assumption
		// that if there was an error, the user may be fixing it.
		// Also, during typing, there are intermediate states that are
		// invalid that we don't want to flag to the user, e.g. the
		// text starts with "5", the user deletes that, leaving ""
		// (which is invalid), then types "-" (also invalid), but then
		// the text becomes "-1", which may very well be valid.
		this.errorText = '';
	}

	// TP-3378,  How do I hate thee?  Let me count the ways...
	public onKeyEvent(event: KeyboardEvent) {
		let codeString = event.code as string;
		if (codeString && codeString.toLowerCase() === 'enter') {
			// TP-3378: prevent the "Enter" from being interpreted as "oh, be really annoying and treat this as a 'submit' of the default button"
			event.preventDefault();
			// ... nevertheless, DO "leave"/un-focus the <input> text as Enter normally does
			if ((event.target as HTMLElement).blur) {
				(event.target as HTMLElement).blur();
			}
		}
	}

	public onTextBlur(): void {
		if (this.textFormControl.valid) {
			this.onNumberSpinnerTouched();
			const n = this.stringToNumber(this.textFormControl.value);
			if (typeof n === 'number') {
				this.value = n;
				this.onValueChange(this.value);
			}
		} else {
			// Invalid: revert to the last valid value:
			this.textFormControl.setValue('' + this.value);
			this.onValueChange(this.value);
			this.errorText = '';
		}
	}

	private onNumberSpinnerTouched(): void {
		if (this.onTouched) {
			this.onTouched();
		}
		this.updateDisplayedValidityStatus();
	}

	private updateDisplayedValidityStatus(): void {
		if (this.textFormControl.valid) {
			this.errorText = '';
		} else {
			const errors = this.textFormControl.errors;
			if (errors) {
				if (errors.min) {
					this.errorText = strings.NUMBER_SPINNER_VALUE_IS_THE_MINIMUM(this.min);
				} else if (errors.max) {
					this.errorText = strings.NUMBER_SPINNER_VALUE_IS_THE_MAXIMUM(this.max);
				} else {
					this.errorText = this.textFormControl.errors.numberFormatError;
				}
			}
		}
	}

	public ngOnDestroy(): void {
		this.valueChangeSub?.unsubscribe();
	}

	private isString(v: any): boolean {
		return typeof v === 'string' || v instanceof String;
	}

	/**
	 * An internal method that updates all the same data in the same way as before, but provides a way to opt out of emitting events
	 * for things triggered by a programatic update. As in, a patchValue on the external form should have emitEvent false. As this is
	 * _not_ user input.
	 * @param value value to be set
	 * @param doNotEmitEvent set to true if this update should be considered programatic and not emit an event.
	 */
	private writeValueInternal(value: any, doNotEmitEvent = false) {
		if (value !== null && value !== undefined) {
			if (this.isString(value)) {
				const n = this.stringToNumber(value);
				if (typeof n === 'number') {
					value = n as number;
				} else {
					this.logger.error('writeValue: invalid value: ', value);
					return;
				}
			}
			this.textFormControl.setValue('' + value, { emitEvent: !doNotEmitEvent });
			this.value = value;
			this.updateDisplayedValidityStatus();
		}
	}

	/********** ControlValueAccessor / NG_VALUE_ACCESSOR methods **********/

	/**
	 * This method is called by the forms API to write to the view when programmatic changes from model to view are requested.
	 * https://angular.io/api/forms/ControlValueAccessor#writevalue
	 * We need to forward that value into the <input text>.  Because we use a FormControl to manage
	 * that <input>, we use "setValue" on that internal "textFormControl".
	 */
	public writeValue(value: any): void {
		this.writeValueInternal(value, true);
	}

	/**
	 * This method is called by Angular to give this class a callback method, which is to be called
	 * when the value of this component changes.  Here we listen on the Observable available for the
	 * FormControl we have attached to our "<input>" (i.e. "textFormControl"), and when that Observable
	 * tells us the input text has changed, we pass that new value to the provided callback.
	 */
	public registerOnChange(fn: (thing: any) => void): void {
		this.onValueChange = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	/**
	 * Angular calls this to manage to overall enabled/disabled state of this component.  Translating this
	 * to our internal representation simply means enabling of disabling our one input text component.
	 */
	public setDisabledState(isDisabled: boolean): void {
		if (isDisabled) {
			this.textFormControl.disable();
		} else {
			this.textFormControl.enable();
		}
		this.componentIsDisabled = isDisabled;
	}

	/********** NG_VALIDATORS methods **********/

	public validate({ value }: UntypedFormControl) {
		if (isNaN(value) || value < this.min || value > this.max) {
			return { invalid: true };
		}
		return false;
	}
}
